import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Check, SkipForward, Pipette, RotateCcw, Plus, Trash2, ToggleLeft, ToggleRight, PartyPopper, Lightbulb } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AppIcons } from '../constants/icons';

interface ColorTaggerModalProps {
  items: any[];
  allItems: any[]; // All items including already-tagged ones
  userId: string;
  onClose: () => void;
  onUpdate: () => void;
}

// Convert RGB to hex
const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
};

const ColorTaggerModal: React.FC<ColorTaggerModalProps> = ({ items, allItems, userId, onClose, onUpdate }) => {
  const [showOnlyUntagged, setShowOnlyUntagged] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sampledColors, setSampledColors] = useState<string[]>([]); // Multiple colors!
  const [saving, setSaving] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);
  const [samplePositions, setSamplePositions] = useState<{ x: number; y: number; color: string }[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Filter items based on toggle
  const displayItems = showOnlyUntagged ? items : allItems;
  const currentItem = displayItems[currentIndex];
  const progress = displayItems.length > 0 ? ((currentIndex + 1) / displayItems.length) * 100 : 0;

  // Sample radius - how many pixels around the click point to average
  const SAMPLE_RADIUS = 15; // Creates a ~30x30 pixel sampling area

  // Get the photo URL for current item
  const getPhotoUrl = () => {
    if (currentItem?.photos?.length > 0) {
      const defaultPhoto = currentItem.photos.find((p: any) => p.isDefault);
      return defaultPhoto?.url || currentItem.photos[0].url;
    }
    if (currentItem?.swatchPhotos?.length > 0) {
      return currentItem.swatchPhotos[0];
    }
    return null;
  };

  const photoUrl = getPhotoUrl();

  // Reset when item changes, load existing colors if re-tagging
  useEffect(() => {
    if (currentItem?.colors && currentItem.colors.length > 0) {
      // Load existing colors for re-tagging
      setSampledColors(currentItem.colors);
    } else if (currentItem?.primaryColor) {
      // Legacy: single color
      setSampledColors([currentItem.primaryColor]);
    } else {
      setSampledColors([]);
    }
    setSamplePositions([]);
    setImageLoaded(false);
  }, [currentIndex, currentItem, showOnlyUntagged]);

  // Load image onto canvas when URL changes
  useEffect(() => {
    if (!photoUrl || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Set canvas size to match image aspect ratio
      const maxWidth = 500;
      const maxHeight = 500;
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (maxHeight / height) * width;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      setImageLoaded(true);
    };

    img.onerror = () => {
      console.error('Failed to load image for color sampling');
      setImageLoaded(false);
    };

    img.src = photoUrl;
  }, [photoUrl]);

  // Handle image click to sample color (area average, not single pixel)
  const handleImageClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !imageLoaded) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const centerX = Math.floor((e.clientX - rect.left) * scaleX);
    const centerY = Math.floor((e.clientY - rect.top) * scaleY);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Sample a circular area and average the colors
    let totalR = 0, totalG = 0, totalB = 0;
    let pixelCount = 0;

    // Get image data for the sampling area
    const startX = Math.max(0, centerX - SAMPLE_RADIUS);
    const startY = Math.max(0, centerY - SAMPLE_RADIUS);
    const width = Math.min(SAMPLE_RADIUS * 2, canvas.width - startX);
    const height = Math.min(SAMPLE_RADIUS * 2, canvas.height - startY);
    
    const imageData = ctx.getImageData(startX, startY, width, height);
    const data = imageData.data;

    // Loop through pixels in a circular pattern
    for (let dy = -SAMPLE_RADIUS; dy <= SAMPLE_RADIUS; dy++) {
      for (let dx = -SAMPLE_RADIUS; dx <= SAMPLE_RADIUS; dx++) {
        // Check if pixel is within circle
        if (dx * dx + dy * dy <= SAMPLE_RADIUS * SAMPLE_RADIUS) {
          const px = centerX + dx;
          const py = centerY + dy;
          
          // Check bounds
          if (px >= 0 && px < canvas.width && py >= 0 && py < canvas.height) {
            // Calculate position in imageData array
            const localX = px - startX;
            const localY = py - startY;
            const idx = (localY * width + localX) * 4;
            
            if (idx >= 0 && idx < data.length - 2) {
              totalR += data[idx];
              totalG += data[idx + 1];
              totalB += data[idx + 2];
              pixelCount++;
            }
          }
        }
      }
    }

    if (pixelCount === 0) return;

    // Calculate average color
    const r = Math.round(totalR / pixelCount);
    const g = Math.round(totalG / pixelCount);
    const b = Math.round(totalB / pixelCount);
    const hexColor = rgbToHex(r, g, b);

    // Add to colors array if not already present
    if (!sampledColors.includes(hexColor)) {
      setSampledColors([...sampledColors, hexColor]);
    }
    
    // Track position for visual feedback
    setSamplePositions([...samplePositions, { 
      x: e.clientX - rect.left, 
      y: e.clientY - rect.top, 
      color: hexColor 
    }]);
  };

  // Handle mouse move for cursor position
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    setCursorPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Save and move to next
  const handleSaveAndNext = async () => {
    if (sampledColors.length === 0) {
      alert('Please click on the image to sample at least one color!');
      return;
    }

    setSaving(true);
    try {
      const itemRef = doc(db, 'users', userId, 'items', currentItem.id);
      
      // Save all colors, with primaryColor being the first one
      await updateDoc(itemRef, { 
        primaryColor: sampledColors[0],
        colors: sampledColors
      });
      
      if (currentIndex < displayItems.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        // Done with all items!
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Error saving color:', error);
      alert('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Skip current item
  const handleSkip = () => {
    if (currentIndex < displayItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onClose();
    }
  };

  // Navigate
  const goToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < displayItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Reset selection
  const handleReset = () => {
    setSampledColors([]);
    setSamplePositions([]);
  };

  // Remove a specific color
  const removeColor = (colorToRemove: string) => {
    setSampledColors(sampledColors.filter(c => c !== colorToRemove));
    setSamplePositions(samplePositions.filter(p => p.color !== colorToRemove));
  };

  // No items to tag
  if (!currentItem || displayItems.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="glass-modal p-8 text-center max-w-md">
          <PartyPopper className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2 font-heading text-glass">
            {showOnlyUntagged ? 'All Done!' : 'No Items Found'}
          </h2>
          <p className="text-glass-secondary mb-4 font-body">
            {showOnlyUntagged
              ? 'All your items with photos have been color tagged!'
              : 'No items with photos found.'}
          </p>
          {showOnlyUntagged && allItems.length > 0 && (
            <button
              onClick={() => {
                setShowOnlyUntagged(false);
                setCurrentIndex(0);
              }}
              className="px-6 py-3 bg-purple-600/80 text-white rounded-full font-bold hover:bg-purple-600 transition-default mb-3 w-full font-body"
            >
              Re-tag Existing Colors
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-3 bg-slate-700 text-white rounded-full font-bold hover:bg-slate-600 transition-default w-full font-body"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tagger-overlay">
      {/* Header */}
      <div className="tagger-header">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="tagger-btn-close"
          >
            <X className="icon-base" />
          </button>
          <div>
            <h2 className="text-lg font-bold font-heading text-glass">Color Tagger</h2>
            <p className="text-sm text-glass-secondary font-body">
              {currentIndex + 1} of {displayItems.length} items
            </p>
          </div>
        </div>

        {/* Toggle: Show only untagged */}
        <button
          onClick={() => {
            setShowOnlyUntagged(!showOnlyUntagged);
            setCurrentIndex(0);
          }}
          className={`hidden sm:flex tagger-toggle font-body ${
            showOnlyUntagged ? 'tagger-toggle-untagged' : 'tagger-toggle-all'
          }`}
        >
          {showOnlyUntagged ? (
            <>
              <ToggleLeft className="icon-sm" />
              Untagged Only
            </>
          ) : (
            <>
              <ToggleRight className="icon-sm" />
              All Polishes
            </>
          )}
        </button>

        {/* Progress bar */}
        <div className="flex-1 max-w-xs mx-4">
          <div className="tagger-progress-track">
            <div
              className="tagger-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrev}
            disabled={currentIndex === 0}
            className="tagger-btn-nav"
          >
            <ChevronLeft className="icon-base" />
          </button>
          <button
            onClick={goToNext}
            disabled={currentIndex === displayItems.length - 1}
            className="tagger-btn-nav"
          >
            <ChevronRight className="icon-base" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto p-4 flex flex-col lg:flex-row gap-6 items-center justify-center">
        {/* Left: Image */}
        <div className="tagger-card">
          {/* Item info */}
          <div className="text-center mb-4">
            <p className="text-glass font-bold text-lg font-heading">
              {currentItem.brand}
            </p>
            <h3 className="text-xl font-bold text-glass font-body">
              {currentItem.name}
            </h3>
          </div>

          {/* Canvas for color picking */}
          {photoUrl ? (
            <div className="relative">
              <canvas
                ref={canvasRef}
                onClick={handleImageClick}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setCursorPos(null)}
                className="rounded-xl cursor-crosshair max-w-full"
                style={{ maxHeight: '400px' }}
              />

              {/* Hover cursor indicator - shows sampling area size */}
              {cursorPos && imageLoaded && (
                <div
                  className="pointer-events-none absolute rounded-full border-2 border-white"
                  style={{
                    left: cursorPos.x - 15,
                    top: cursorPos.y - 15,
                    width: 30,
                    height: 30,
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.5), inset 0 0 8px rgba(255,255,255,0.3)'
                  }}
                />
              )}

              {/* Sample position indicators - shows where you clicked */}
              {samplePositions.map((pos, idx) => (
                <div
                  key={idx}
                  className="pointer-events-none absolute rounded-full"
                  style={{
                    left: pos.x - 18,
                    top: pos.y - 18,
                    width: 36,
                    height: 36,
                    border: `4px solid ${pos.color}`,
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.8), 0 0 10px rgba(0,0,0,0.3)'
                  }}
                >
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-white rounded-full text-xs font-bold flex items-center justify-center shadow">
                    {idx + 1}
                  </span>
                </div>
              ))}

              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center border-2 border-gray-300 rounded-xl min-h-[300px] min-w-[300px]">
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border border-gray-300 border-t-slate-500 rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-sm text-glass-secondary">Loading image...</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-80 h-80 rainbow-gradient rounded-xl flex items-center justify-center">
              <AppIcons.Polish className="w-24 h-24 text-glass-secondary" />
            </div>
          )}

          {/* Instructions */}
          <div className="mt-4 text-center">
            <div className="tagger-instruction">
              <Pipette className="icon-sm" />
              <span className="font-body">
                Click to add colors • Magnetics/thermals can have multiple!
              </span>
            </div>
          </div>
        </div>

        {/* Right: Color results & actions */}
        <div className="tagger-card w-full lg:w-80">
          {/* Sampled colors display */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="tagger-section-header font-body">
                Captured Colors ({sampledColors.length})
              </h4>
              {sampledColors.length > 0 && (
                <button
                  onClick={handleReset}
                  className="text-xs text-glass-secondary hover:text-red-500 flex items-center gap-1"
                >
                  <RotateCcw className="icon-xs" /> Clear all
                </button>
              )}
            </div>

            {sampledColors.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {sampledColors.map((color, idx) => (
                  <div
                    key={idx}
                    className="tagger-color-item group"
                  >
                    <div
                      className="tagger-color-swatch"
                      style={{ backgroundColor: color }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono font-bold text-glass text-sm">
                        {color}
                      </p>
                      <p className="text-xs text-glass-secondary">
                        Color {idx + 1}{idx === 0 ? ' (primary)' : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => removeColor(color)}
                      className="p-1.5 text-glass-secondary hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tagger-empty">
                <div className="w-12 h-12 rounded-xl border-2 border-dashed border-slate-500/50 mx-auto mb-2 flex items-center justify-center">
                  <Plus className="icon-base text-glass-secondary" />
                </div>
                <p className="text-glass-secondary text-sm font-body">
                  Click on the image to sample colors
                </p>
              </div>
            )}
          </div>

          {/* Color strip preview */}
          {sampledColors.length > 1 && (
            <div className="mb-4">
              <p className="text-xs text-glass-secondary mb-2">Color preview:</p>
              <div className="h-8 rounded-lg overflow-hidden flex shadow-inner border border-slate-500/50">
                {sampledColors.map((color, idx) => (
                  <div
                    key={idx}
                    className="flex-1 h-full"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Info about multiple colors */}
          <div className="tagger-tip mb-4">
            <p className="text-xs font-body flex items-center gap-2">
              <Lightbulb className="icon-sm" /> For magnetics, thermals, and multichromes - click multiple spots to capture all the colors!
            </p>
          </div>

          {/* Toggle for re-tagging (mobile) */}
          <button
            onClick={() => {
              setShowOnlyUntagged(!showOnlyUntagged);
              setCurrentIndex(0);
            }}
            className={`sm:hidden w-full mb-3 tagger-toggle justify-center font-body ${
              showOnlyUntagged ? 'tagger-toggle-untagged' : 'tagger-toggle-all'
            }`}
          >
            {showOnlyUntagged ? (
              <>
                <ToggleLeft className="icon-sm" />
                Showing Untagged Only
              </>
            ) : (
              <>
                <ToggleRight className="icon-sm" />
                Showing All Polishes
              </>
            )}
          </button>

          {/* Actions */}
          <div className="space-y-2">
            <button
              onClick={handleSaveAndNext}
              disabled={saving || sampledColors.length === 0}
              className="tagger-btn-save font-body"
            >
              {saving ? (
                <>Saving...</>
              ) : currentIndex < displayItems.length - 1 ? (
                <>
                  <Check className="icon-md" />
                  Save {sampledColors.length} Color{sampledColors.length !== 1 ? 's' : ''} & Next
                </>
              ) : (
                <>
                  <Check className="icon-md" />
                  Save & Finish
                </>
              )}
            </button>
            <button
              onClick={handleSkip}
              className="tagger-btn-skip font-body"
            >
              <SkipForward className="icon-md" />
              Skip for now
            </button>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="mt-4 text-center">
            <p className="text-xs text-glass-secondary font-body">
              Tip: You can close anytime - progress is saved!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorTaggerModal;
